/**
 * SitoPersonaleIntroModal — annuncio one-time del "sito personale" feature
 * per gli agenti PRO che ancora non l'hanno configurato.
 *
 * iter v32 r37u · User M1453 — Onboarding step dedicato per chiarire che
 * Pronto offre un SITO PERSONALE (non un profilo dentro un portale),
 * cosa unica in Italia. Auto-mostra UNA VOLTA per ogni agente che:
 *   - ha plan Pro/Pro Agency/Studio (o è founder)
 *   - non ha ancora `public_slug` configurato
 *   - non ha già dismissed il modal (localStorage `pronto_sito_intro_seen_v2`)
 *
 * Una volta chiuso, non riappare (dismiss permanente).
 *
 * iter v33 r106 · TRIGGER CONTESTUALE — l'utente non deve vedere il modal
 * al primo login (era invasivo, si sovrapponeva a ProfileSetupWizard).
 * Ora appare SOLO quando l'utente entra per la prima volta nella sezione
 * Profilo (/dashboard/profile) — momento in cui è più rilevante scoprire
 * "hai un sito personale che puoi configurare".
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Link, useLocation } from "react-router-dom";
import {
  X, Globe, SealCheck, At, WhatsappLogo, IdentificationCard,
  ArrowRight, Sparkle,
} from "@phosphor-icons/react";

const SEEN_KEY = "pronto_sito_intro_seen_v2";

const USAGES = [
  { Icon: At,                 label: "Bio Instagram",          sub: "Un solo link, sempre aggiornato" },
  { Icon: WhatsappLogo,       label: "Stato WhatsApp Business", sub: "Apri direttamente dal contatto" },
  { Icon: IdentificationCard, label: "Biglietti da visita",    sub: "Affianca al QR · tutto in una pagina" },
  { Icon: Globe,              label: "Firma email",            sub: "Sotto al numero, sempre visibile" },
];

export default function SitoPersonaleIntroModal() {
  const { user } = useAuth();
  const location = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(SEEN_KEY)) return;
    const plan = (user.plan || "free").toLowerCase();
    const looksPro = plan === "pro" || plan === "pro_agency" || plan === "studio";
    if (!looksPro) return;
    if (user.public_slug) return; // già configurato
    // iter v33 r106 · Trigger contestuale: aspetta l'ingresso in /dashboard/profile
    // per non affollare il primo login con multipli modali.
    if (!location.pathname.startsWith("/dashboard/profile")) return;
    // Mostra dopo un piccolo delay per non bloccare il primo render
    const t = setTimeout(() => setShow(true), 800);
    return () => clearTimeout(t);
  }, [user, location.pathname]);

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch (_) { /* storage piena/disabilitata: dismiss in-memory soltanto */ }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
      style={{ background: "rgba(28,28,26,0.55)", backdropFilter: "blur(4px)" }}
      onClick={dismiss}
      data-testid="sito-intro-modal"
    >
      <div
        className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 duration-300"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "92vh", overflowY: "auto" }}
      >
        {/* Header decorativo brand */}
        <div className="relative h-32 sm:h-36 overflow-hidden" style={{ background: "linear-gradient(135deg, #1C1C1A 0%, #2F3530 60%, #5F6B53 100%)" }}>
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-30 blur-2xl" style={{ background: "#7C8B6E" }} />
          <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full opacity-20 blur-2xl" style={{ background: "#C8B886" }} />
          <button
            type="button"
            onClick={dismiss}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
            aria-label="Chiudi"
            data-testid="sito-intro-close"
          >
            <X size={16} weight="bold" />
          </button>
          <div className="relative h-full flex items-end p-5 sm:p-6">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white text-[#1C1C1A] text-[9.5px] uppercase tracking-[0.18em] font-bold mb-2">
                <SealCheck size={11} weight="fill" />
                Novità · Unico in Italia
              </div>
              <h2 className="font-editorial text-[24px] sm:text-[30px] leading-[1.05] text-white">
                Hai un sito <span className="italic text-[#C8B886]">tutto tuo.</span>
              </h2>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-6">
          <p className="text-[14.5px] leading-relaxed text-[#3F3E3A] mb-5">
            Pronto ti offre <strong>il primo sito personale</strong> per agenti immobiliari in Italia.
            Un dominio al tuo nome con bio, contatti e immobili sempre aggiornati. Non un profilo dentro un portale altrui: <strong>è davvero il tuo</strong>.
          </p>

          {/* URL preview */}
          <div className="rounded-lg border border-[#E5E1D8] bg-[#FAFAF7] p-3 mb-5 font-mono text-[13px] sm:text-[15px] text-[#1C1C1A]">
            prontoimmo.it/agente/<span className="italic text-[#5F6B53]">il-tuo-nome</span>
          </div>

          {/* Usages */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6" data-testid="sito-intro-usages">
            {USAGES.map(({ Icon, label, sub }) => (
              <div key={label} className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-md border border-[#E5E1D8] flex items-center justify-center flex-shrink-0">
                  <Icon size={14} weight="regular" className="text-[#7C8B6E]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-[#1C1C1A] leading-tight">{label}</div>
                  <div className="text-[11.5px] text-[#6B6A65] leading-tight mt-0.5">{sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Microcopy stats */}
          <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[11.5px] text-[#5F6B53] mb-5 items-center">
            <span className="inline-flex items-center gap-1"><Sparkle size={11} weight="fill" /> 4 estetiche editoriali</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">Configurazione in 2 minuti</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">Incluso nel Pro</span>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row-reverse gap-2.5">
            <Link
              to="/dashboard/vetrina-agente"
              onClick={dismiss}
              data-testid="sito-intro-cta"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1C1C1A] hover:bg-[#000] text-white px-5 py-3 rounded-full text-[13.5px] font-semibold transition-all"
            >
              Configura il mio sito <ArrowRight size={14} weight="bold" />
            </Link>
            <button
              type="button"
              onClick={dismiss}
              data-testid="sito-intro-later"
              className="flex-1 sm:flex-initial sm:px-4 inline-flex items-center justify-center text-[12.5px] font-medium text-[#6B6A65] hover:text-[#1C1C1A] py-3 rounded-full"
            >
              Magari più tardi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
